/**
 * Navigation helper utilities.
 *
 * Provides convenient helper functions for common navigation patterns.
 * Reduces boilerplate and centralizes route construction.
 *
 * @example
 * ```tsx
 * const nav = useNavigationHelpers();
 * nav.navigateToTenant(tenantId);
 * nav.navigateToLeasing();
 * nav.navigateToCommunications({ recipient: 'john@example.com' });
 * ```
 */

import { useNavigate, useLocation } from '../routes';
import { useNavigateTo } from './typeSafeNavigation';
import { ROUTES } from '@repo/shared-types/routeRegistry';

/**
 * Hook that provides convenient navigation helper functions.
 *
 * Combines type-safe navigation for static routes with dynamic route helpers.
 *
 * @returns Object containing navigation helper functions
 */
export const useNavigationHelpers = () => {
  const navigate = useNavigate();
  const navigateTo = useNavigateTo();

  return {
    // Type-safe static routes
    /**
     * Navigates to the main dashboard.
     */
    navigateToDashboard: () => navigateTo('/app'),

    /**
     * Navigates to the leasing dashboard.
     */
    navigateToLeasing: () => navigateTo('/app/leasing'),

    /**
     * Navigates to the maintenance/work orders view.
     */
    navigateToMaintenance: () => navigateTo('/app/maintenance'),

    /**
     * Navigates to the accounting bills view.
     */
    navigateToAccounting: () => navigateTo(ROUTES.OPS_ACCOUNTING_BILLS),

    /**
     * Navigates to the properties directory.
     */
    navigateToProperties: () => navigateTo('/app/properties'),

    /**
     * Navigates to the tenants directory.
     */
    navigateToTenants: () => navigateTo('/app/tenants'),

    /**
     * Navigates to the workflows view.
     */
    navigateToWorkflows: () => navigateTo('/app/workflows'),

    /**
     * Navigates to the reporting view.
     */
    navigateToReporting: () => navigateTo('/app/reporting'),

    /**
     * Navigates to the communications view.
     */
    navigateToCommunications: () => navigateTo('/app/communications'),

    // Dynamic routes (use navigate directly)
    /**
     * Navigates to a specific tenant's profile page.
     * @param tenantId - The ID of the tenant to view
     */
    navigateToTenant: (tenantId: string) => navigate(`/app/tenants/${tenantId}`),

    /**
     * Navigates to a specific property's detail page.
     * @param propertyId - The ID of the property to view
     */
    navigateToProperty: (propertyId: string) => navigate(`/app/properties/${propertyId}`),

    /**
     * Navigates to a specific job's detail page.
     * @param jobId - The ID of the job to view
     */
    navigateToJob: (jobId: string) => navigate(`/app/jobs/${jobId}`),

    /**
     * Navigates to a specific vendor's profile page.
     * @param vendorId - The ID of the vendor to view
     */
    navigateToVendor: (vendorId: string) => navigate(`/app/maintenance/vendors/${vendorId}`),

    /**
     * Navigates to a specific project's detail page.
     * @param projectId - The ID of the project to view
     */
    navigateToProject: (projectId: string) => navigate(`/app/maintenance/projects/${projectId}`),

    // Routes with query parameters
    /**
     * Navigates to the reporting view, optionally filtered by property.
     * @param propertyId - Optional property ID to filter reports
     */
    navigateToReportingWithProperty: (propertyId: string) => {
      navigate(`/app/reporting?propertyId=${propertyId}`);
    },

    // Common navigation patterns with state
    /**
     * Navigates to the communications view with optional state.
     * @param state - Optional state object containing recipient and/or subject
     */
    navigateToCommunicationsWithState: (state?: { recipient?: string; subject?: string }) => {
      navigate('/app/communications', { state });
    },
  };
};

/**
 * Hook to navigate to a path with returnTo param for context-aware back navigation.
 * Automatically captures the current path as the returnTo value.
 *
 * @example
 * ```tsx
 * const navigateWithReturn = useNavigateWithReturn();
 * navigateWithReturn('/app/tenants/123', { tab: 'financials' });
 * // Navigates to: /app/tenants/123?returnTo=/app/properties/456&tab=financials
 * ```
 */
export const useNavigateWithReturn = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (path: string, additionalParams?: Record<string, string>) => {
    const params = new URLSearchParams();
    params.set('returnTo', location.pathname + location.search);

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    const queryString = params.toString();
    navigate(`${path}${queryString ? `?${queryString}` : ''}`);
  };
};

/**
 * Hook to get the return path from URL params with fallback.
 * Provides a goBack function that navigates to returnTo if present, otherwise falls back.
 *
 * @param fallbackPath - Default path if returnTo is not present
 * @returns Object with goBack function and returnTo value
 *
 * @example
 * ```tsx
 * const { goBack } = useBackNavigation('/app/properties');
 * <button onClick={goBack}>Back</button>
 * ```
 */
export const useBackNavigation = (fallbackPath: string) => {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    const searchParams = new URLSearchParams(location.search);
    const returnTo = searchParams.get('returnTo');

    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(fallbackPath);
    }
  };

  return { goBack, returnTo: new URLSearchParams(location.search).get('returnTo') };
};
